import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Badge,
  Button,
  Chip,
  Divider,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Circle as CircleIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../services/notificationService';

const Notifications = () => {
  const theme = useTheme();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading, refetch } = useQuery(
    'notifications',
    () => getNotifications(false),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  const markAsReadMutation = useMutation(
    (notificationId) => markNotificationAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotificationCount');
      },
    }
  );

  const markAllAsReadMutation = useMutation(
    () => markAllNotificationsAsRead(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unreadNotificationCount');
      },
    }
  );

  const unreadCount = notifications.filter((n) => n.is_read === 0).length;
  const unreadNotifications = notifications.filter((n) => n.is_read === 0);
  const readNotifications = notifications.filter((n) => n.is_read === 1);
  
  const parseNotificationMessage = (message, type) => {
    // Extract key information from message
    const info = {
      amount: null,
      deadline: null,
      tier: null,
      eventTitle: null,
    };
    
    // Extract amount
    const amountMatch = message.match(/\$(\d+\.?\d*)/);
    if (amountMatch) {
      info.amount = amountMatch[1];
    }
    
    // Extract deadline
    const deadlineMatch = message.match(/by ([A-Za-z]+ \d{1,2}, \d{4})/);
    if (deadlineMatch) {
      info.deadline = deadlineMatch[1];
    }
    
    // Extract tier
    const tierMatch = message.match(/Tier: ([^.]+)/);
    if (tierMatch) {
      info.tier = tierMatch[1].trim();
    }
    
    return info;
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (notification.is_read === 0) {
      markAsReadMutation.mutate(notification.notification_id);
    }

    // Handle different notification types
    if (notification.type === 'new_application' && notification.event_id && notification.application_id) {
      // For organizers: new application notification -> go to CRM
      history.push(`/events/${notification.event_id}/crm`);
      return;
    }
    
    // If notification is about payment and has an application_id, navigate to payment page (for vendors)
    if ((notification.type?.includes('approved') || notification.type?.includes('accepted')) && 
        notification.application_id && 
        notification.message?.includes('payment')) {
      history.push(`/applications/${notification.application_id}/payment`);
      return;
    }
    
    // Navigate to event detail page if event exists
    if (notification.event_id) {
      history.push(`/event/${notification.event_id}`);
    } else if (notification.application_id) {
      // Could navigate to application detail page if it exists
      history.push('/applications');
    }
  };

  const getNotificationIcon = (type) => {
    if (type?.includes('approved') || type?.includes('accepted')) {
      return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
    } else if (type?.includes('rejected')) {
      return <CancelIcon sx={{ color: theme.palette.error.main }} />;
    } else if (type === 'new_application') {
      return <EventIcon sx={{ color: theme.palette.info.main }} />;
    } else if (type?.includes('payment')) {
      return <PaymentIcon sx={{ color: theme.palette.success.main }} />;
    }
    return <EventIcon sx={{ color: theme.palette.primary.main }} />;
  };

  const getNotificationColor = (type) => {
    if (type?.includes('approved') || type?.includes('accepted') || type?.includes('payment')) {
      return 'success';
    } else if (type?.includes('rejected')) {
      return 'error';
    } else if (type === 'new_application') {
      return 'info';
    }
    return 'primary';
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            </Badge>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<CheckIcon />}
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isLoading}
            >
              Mark All as Read
            </Button>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            You don't have any notifications yet.
          </Alert>
        ) : (
          <>
            {/* Unread Notifications */}
            {unreadNotifications.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main, fontWeight: 600 }}>
                  New ({unreadNotifications.length})
                </Typography>
                <List>
                  {unreadNotifications.map((notification, index) => (
                    <React.Fragment key={notification.notification_id}>
                      <ListItem
                        button
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 2,
                          mb: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                            boxShadow: 1,
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette[getNotificationColor(notification.type)].light }}>
                            {getNotificationIcon(notification.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {notification.title}
                              </Typography>
                              <CircleIcon sx={{ fontSize: 8, color: theme.palette.primary.main }} />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                {notification.message}
                              </Typography>
                              
                              {/* Extract and display key info */}
                              {(() => {
                                const info = parseNotificationMessage(notification.message, notification.type);
                                const hasDetails = info.amount || info.deadline || info.tier;
                                
                                if (hasDetails && (notification.type?.includes('accepted') || notification.type?.includes('approved'))) {
                                  return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                      {info.amount && (
                                        <Chip
                                          icon={<PaymentIcon />}
                                          label={`$${info.amount}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )}
                                      {info.deadline && (
                                        <Chip
                                          label={`Due: ${info.deadline}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                        />
                                      )}
                                      {info.tier && (
                                        <Chip
                                          label={info.tier}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  );
                                }
                                return null;
                              })()}
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(notification.created_at), 'MMM d, yyyy • h:mm a')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {notification.event && (
                                    <Chip
                                      label={notification.event.title}
                                      size="small"
                                      icon={<EventIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        history.push(`/event/${notification.event_id}`);
                                      }}
                                    />
                                  )}
                                  {notification.application_id && (
                                    <Chip
                                      label="View Application"
                                      size="small"
                                      color="primary"
                                      variant="filled"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        history.push('/applications');
                                      }}
                                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReadMutation.mutate(notification.notification_id);
                          }}
                        >
                          <CheckIcon />
                        </IconButton>
                      </ListItem>
                      {index < unreadNotifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}

            {/* Read Notifications */}
            {readNotifications.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                  Earlier
                </Typography>
                <List>
                  {readNotifications.map((notification, index) => (
                    <React.Fragment key={notification.notification_id}>
                      <ListItem
                        button
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.grey[300] }}>
                            {getNotificationIcon(notification.type)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {notification.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                {notification.message}
                              </Typography>
                              
                              {/* Extract and display key info */}
                              {(() => {
                                const info = parseNotificationMessage(notification.message, notification.type);
                                const hasDetails = info.amount || info.deadline || info.tier;
                                
                                if (hasDetails && (notification.type?.includes('accepted') || notification.type?.includes('approved'))) {
                                  return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                      {info.amount && (
                                        <Chip
                                          icon={<PaymentIcon />}
                                          label={`$${info.amount}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )}
                                      {info.deadline && (
                                        <Chip
                                          label={`Due: ${info.deadline}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                        />
                                      )}
                                      {info.tier && (
                                        <Chip
                                          label={info.tier}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  );
                                }
                                return null;
                              })()}
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(notification.created_at), 'MMM d, yyyy • h:mm a')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {notification.event && (
                                    <Chip
                                      label={notification.event.title}
                                      size="small"
                                      icon={<EventIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        history.push(`/event/${notification.event_id}`);
                                      }}
                                    />
                                  )}
                                  {notification.application_id && (
                                    <Chip
                                      label="View Application"
                                      size="small"
                                      color="primary"
                                      variant="filled"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        history.push('/applications');
                                      }}
                                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < readNotifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Notifications;

